<script setup>
import { Link } from '@inertiajs/vue3'
import { ChevronRight } from 'lucide-vue-next'
import SnapRail from '@/Components/ui/SnapRail.vue'
import { tutors, subjects as subjectsRoute } from '@/routes/index'
import { subjectIcon } from '@/lib/subjectIcon'

/**
 * A subject rail that only ever shows subjects somebody teaches — the
 * controller filters on that — so every tile leads to a listing with tutors in
 * it.
 *
 * It was a wrapping grid; it is now the same scroll-snap rail as the tutors
 * above, so the two sections behave the same way and neither pushes the page
 * down by four rows on a phone.
 *
 * Icons come from the database but are resolved through an allow list first:
 * the icon font prints an unknown name as text, which is how Chemistry came to
 * say "FLASK" across its tile.
 */
defineProps({
  subjects: {
    type: Array,
    default: () => [],
  },
})

const subjectUrl = (subject) => tutors.url({ query: { subject: subject.slug } })
</script>

<template>
  <section v-if="subjects.length" id="subjects" class="scroll-mt-24 pb-14 md:pb-20">
    <div class="container-page">
      <!--
        Two rows on desktop rather than one long rail. Twelve tiles in a 6 × 2
        grid fill the width without asking anyone to scroll sideways for
        something they can see all of; below `lg` it stays a swipe rail, which
        is the right shape on a phone.
      -->
      <div class="hidden lg:block">
        <div v-reveal class="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 class="text-2xl md:text-3xl">Find tutors for any subject</h2>
            <p class="mt-2 text-[15px] text-muted">Expert tutors across GCSE, A-Level and beyond.</p>
          </div>
          <Link :href="subjectsRoute()" class="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
            View all subjects <ChevronRight class="size-4" />
          </Link>
        </div>

        <div class="grid grid-cols-6 gap-4">
          <Link
            v-for="(subject, index) in subjects.slice(0, 12)"
            :key="subject.id"
            v-reveal="{ delay: Math.min(index, 6) * 45 }"
            :href="subjectUrl(subject)"
            class="group flex flex-col items-center gap-3 rounded-lg border border-line bg-surface p-5 text-center transition duration-200 ease-brand hover:-translate-y-0.5 hover:border-line-brand hover:bg-surface-brand hover:shadow-sm"
          >
            <span class="grid size-12 place-items-center rounded-full bg-brand-soft text-brand transition-transform duration-200 ease-brand group-hover:scale-110">
              <span class="material-symbols-outlined text-2xl" aria-hidden="true">{{ subjectIcon(subject) }}</span>
            </span>
            <p class="text-sm font-semibold text-ink">{{ subject.name }}</p>
          </Link>
        </div>
      </div>

      <SnapRail
        class="lg:hidden"
        label="subjects"
        :items="subjects"
        item-class="w-[42%] sm:w-[26%] md:w-[20%]"
      >
        <template #heading>
          <div v-reveal>
            <h2 class="text-2xl md:text-3xl">Find tutors for any subject</h2>
            <p class="mt-2 text-[15px] text-muted">Expert tutors across GCSE, A-Level and beyond.</p>
          </div>
        </template>

        <template #controls>
          <Link :href="subjectsRoute()" class="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
            View all subjects <ChevronRight class="size-4" />
          </Link>
        </template>

        <template #item="{ item, index }">
          <Link
            v-reveal="{ delay: Math.min(index, 6) * 50 }"
            :href="subjectUrl(item)"
            class="group flex h-full flex-col items-center gap-3 rounded-lg border border-line bg-surface p-5 text-center transition duration-200 ease-brand hover:-translate-y-0.5 hover:border-line-brand hover:bg-surface-brand hover:shadow-sm"
          >
            <span class="grid size-12 place-items-center rounded-full bg-brand-soft text-brand transition-transform duration-200 ease-brand group-hover:scale-110">
              <span class="material-symbols-outlined text-2xl" aria-hidden="true">{{ subjectIcon(item) }}</span>
            </span>
            <p class="text-sm font-semibold text-ink">{{ item.name }}</p>
          </Link>
        </template>
      </SnapRail>
    </div>
  </section>
</template>
